import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CAPABILITY_LABEL, type AiCapability, type AiOverride, type AiProviderDef, type AiProviderId, type AiSettings } from "@gk/shared";
import { api } from "../../lib/api";
import { fmtDate, fmtDuration, useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { ErrorNote } from "../../components/ui";
import { roleLabel } from "../../components/Shell";
import { AdminTabs, AiChoiceEditor, ServeGrid, VendorChip, usd, type ProviderStatusMap } from "./common";

interface ClientRow {
  id: string;
  slug: string;
  name: string;
  plan: string;
  region: string;
  status: string;
  createdAt: string;
  members: number;
  month: string;
  interviewMinutes: number;
  aiCostUsd: number;
  aiOverride: AiOverride | null;
  aiProviders: AiProviderId[];
}

export function AdminClientsPage() {
  const { refreshMe, config } = useSession();
  const nav = useNavigate();
  const list = useAsync(() => api.get<ClientRow[]>("/api/admin/clients"), []);
  const [f, setF] = useState({ name: "", slug: "", plan: "field", region: "us" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/tenants", f);
      setF({ name: "", slug: "", plan: "field", region: "us" });
      await list.reload();
      await refreshMe();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };
  const totals = (list.data ?? []).reduce((a, c) => ({ minutes: a.minutes + c.interviewMinutes, cost: a.cost + c.aiCostUsd }), { minutes: 0, cost: 0 });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Admin panel</h1>
          <p className="sub">Each client has its own database, encryption key and storage. Nothing is shared between clients.</p>
        </div>
      </div>
      <AdminTabs />
      <div className="stack" style={{ gap: 20 }}>
        <div className="figures" role="list">
          <div role="listitem">
            <b>{(list.data ?? []).filter((c) => c.status === "active").length}</b>
            <span>active clients</span>
          </div>
          <div role="listitem">
            <b>{totals.minutes.toLocaleString()}</b>
            <span>interview minutes this month</span>
          </div>
          <div role="listitem">
            <b>{usd(totals.cost)}</b>
            <span>estimated AI spend this month</span>
          </div>
        </div>
        <ErrorNote error={list.error} />
        <section className="panel flush">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Plan</th>
                <th>People</th>
                <th>Interviews this month</th>
                <th>AI spend this month</th>
                <th>AI providers</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((x) => (
                <tr key={x.id} className="click" onClick={() => nav(`/admin/clients/${x.id}`)}>
                  <td>
                    <Link to={`/admin/clients/${x.id}`} style={{ fontWeight: 600, textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>
                      {x.name}
                    </Link>
                    <div className="xs muted">
                      {x.slug} {x.status !== "active" && <span className="pill red">{x.status}</span>}
                    </div>
                  </td>
                  <td>{config.plans[x.plan]?.label ?? x.plan}</td>
                  <td>{x.members}</td>
                  <td>{x.interviewMinutes} min</td>
                  <td>{usd(x.aiCostUsd)}</td>
                  <td>
                    <div className="row wrap" style={{ gap: 4 }}>
                      {x.aiProviders.map((p) => (
                        <VendorChip key={p} id={p} />
                      ))}
                      {x.aiOverride && <span className="xs muted">own choice</span>}
                    </div>
                  </td>
                  <td>{fmtDate(x.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <form className="panel stack" onSubmit={create}>
          <h3>New client workspace</h3>
          <div className="grid2">
            <label className="field">
              <span>Client name</span>
              <input
                className="input"
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) })}
                required
              />
            </label>
            <label className="field">
              <span>Workspace address</span>
              <input className="input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} pattern="[a-z][a-z0-9-]{2,30}" required />
            </label>
            <label className="field">
              <span>Plan</span>
              <select className="input" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
                {Object.entries(config.plans).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Data region</span>
              <input className="input" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} required />
            </label>
          </div>
          <ErrorNote error={error} />
          <div className="row">
            <span className="muted small">Creates the database, encryption key and storage, and makes you its admin.</span>
            <span className="spacer" />
            <button className="btn primary" disabled={busy}>
              {busy ? "Creating…" : "Create workspace"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

interface ClientDetail {
  client: { id: string; slug: string; name: string; plan: string; status: string; region: string; createdAt: string };
  ai: { override: AiOverride | null; effective: AiSettings; providers: AiProviderId[] };
  usage: Array<{ month: string; capability: AiCapability; provider: AiProviderId; model: string; quantity: number; quantityOut: number; calls: number; costUsd: number }>;
  months: Array<{ period: string; interviewSeconds: number }>;
  members: Array<{ id: string; email: string; name: string | null; role: string; disabled: boolean; lastLoginAt: string | null }>;
  remainingSeconds: number | null;
}

interface AiMeta {
  providers: AiProviderDef[];
  settings: AiSettings;
  status: ProviderStatusMap;
  languages: Array<{ code: string; name: string }>;
}

const CAPS: AiCapability[] = ["stt", "translate", "tts", "llm"];

export function AdminClientPage() {
  const { id } = useParams();
  const { config } = useSession();
  const detail = useAsync(() => api.get<ClientDetail>(`/api/admin/clients/${id}`), [id]);
  const meta = useAsync(() => api.get<AiMeta>("/api/admin/ai"), []);
  const [draft, setDraft] = useState<AiSettings | null>(null);
  const [following, setFollowing] = useState<Record<AiCapability, boolean>>({ stt: true, translate: true, tts: true, llm: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!detail.data || !meta.data) return;
    const o = detail.data.ai.override ?? {};
    setFollowing({ stt: !o.stt, translate: !o.translate, tts: !o.tts, llm: !o.llm });
    setDraft(detail.data.ai.effective);
  }, [detail.data, meta.data]);

  if (detail.error) return <ErrorNote error={detail.error} />;
  if (!detail.data || !meta.data || !draft) return <p className="muted">Loading…</p>;
  const d = detail.data;
  const m = meta.data;
  const effective: AiSettings = Object.fromEntries(CAPS.map((c) => [c, following[c] ? m.settings[c] : draft[c]])) as AiSettings;

  const saveAi = async () => {
    setBusy(true);
    setError(null);
    try {
      const override = Object.fromEntries(CAPS.filter((c) => !following[c]).map((c) => [c, draft[c]]));
      await api.put(`/api/admin/clients/${d.client.id}/ai`, { override: Object.keys(override).length ? override : null });
      setSaved(true);
      await detail.reload();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };
  const patch = async (body: { plan?: string; status?: string }) => {
    setError(null);
    try {
      await api.patch(`/api/tenants/${d.client.id}`, body);
      await detail.reload();
    } catch (err) {
      setError(err as Error);
    }
  };
  const byMonth = new Map<string, { cost: number; rows: ClientDetail["usage"] }>();
  for (const u of d.usage) {
    const e = byMonth.get(u.month) ?? { cost: 0, rows: [] };
    e.cost += u.costUsd;
    e.rows.push(u);
    byMonth.set(u.month, e);
  }
  const unitText = (u: ClientDetail["usage"][number]) =>
    u.capability === "stt" ? `${u.quantity.toFixed(1)} min` : u.capability === "llm" ? `${Math.round(u.quantity).toLocaleString()} in / ${Math.round(u.quantityOut).toLocaleString()} out tokens` : `${Math.round(u.quantity).toLocaleString()} characters`;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/admin/clients">Clients</Link> /
          </p>
          <h1>{d.client.name}</h1>
          <p className="sub">
            {d.client.slug} · created {fmtDate(d.client.createdAt)} · region {d.client.region}
          </p>
        </div>
        <span className="spacer" />
        <Link className="btn" to={`/w/${d.client.slug}`}>
          Open workspace
        </Link>
      </div>
      <AdminTabs />
      <div className="stack" style={{ gap: 20 }}>
        <ErrorNote error={error} />
        <section className="panel row wrap" style={{ gap: 20 }}>
          <label className="field" style={{ minWidth: 220 }}>
            <span>Plan</span>
            <select className="input" value={d.client.plan} onChange={(e) => patch({ plan: e.target.value })}>
              {Object.entries(config.plans).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span>Status</span>
            <div className="row">
              <span className={`pill ${d.client.status === "active" ? "green" : "red"}`}>{d.client.status}</span>
              {d.client.status === "active" ? (
                <button className="btn small danger" onClick={() => window.confirm(`Suspend ${d.client.name}? Nobody can sign in to this workspace until it is reactivated.`) && patch({ status: "suspended" })}>
                  Suspend
                </button>
              ) : (
                <button className="btn small" onClick={() => patch({ status: "active" })}>
                  Reactivate
                </button>
              )}
            </div>
          </div>
          {d.remainingSeconds != null && (
            <div className="field">
              <span>Interview time left this month</span>
              <b>{fmtDuration(d.remainingSeconds)}</b>
            </div>
          )}
        </section>

        <section className="panel stack" aria-labelledby="client-ai-h">
          <div className="panel-head">
            <h3 id="client-ai-h">AI providers for this client</h3>
            <p>Tick "Platform default" to follow the platform; untick to choose for this client only (for example when its contract requires one provider).</p>
          </div>
          <AiChoiceEditor
            value={draft}
            onChange={(s) => (setDraft(s), setSaved(false))}
            providers={m.providers}
            status={m.status}
            inherit={{ platform: m.settings, following, onFollowing: (c, f) => (setFollowing({ ...following, [c]: f }), setSaved(false)) }}
          />
          <ServeGrid settings={effective} languages={m.languages} />
          <div className="row">
            {saved && <span className="pill green">Saved</span>}
            <span className="small muted">Applies from this client's next interview.</span>
            <span className="spacer" />
            <button className="btn primary" disabled={busy} onClick={saveAi}>
              {busy ? "Saving…" : "Save for this client"}
            </button>
          </div>
        </section>

        <section className="panel flush" aria-labelledby="usage-h">
          <div className="panel-head" style={{ padding: "16px 20px 0" }}>
            <h3 id="usage-h">Usage and estimated AI spend</h3>
            <p>Last 12 months, per provider and model.</p>
          </div>
          {byMonth.size === 0 ? (
            <p className="muted" style={{ padding: "0 20px 16px" }}>
              No AI usage recorded yet.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Service</th>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>Volume</th>
                  <th>Estimated cost</th>
                </tr>
              </thead>
              <tbody>
                {[...byMonth.entries()].map(([month, e]) =>
                  e.rows.map((u, i) => (
                    <tr key={`${month}-${i}`}>
                      <td>{i === 0 ? <b>{month}</b> : ""}</td>
                      <td>{CAPABILITY_LABEL[u.capability]}</td>
                      <td>
                        <VendorChip id={u.provider} />
                      </td>
                      <td className="small">{u.model === "*" ? "—" : u.model}</td>
                      <td className="small">{unitText(u)}</td>
                      <td>
                        {usd(u.costUsd)}
                        {i === e.rows.length - 1 && <div className="xs muted">Month total {usd(e.cost)}</div>}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          )}
        </section>

        <section className="panel flush" aria-labelledby="members-h">
          <div className="panel-head" style={{ padding: "16px 20px 0" }}>
            <h3 id="members-h">People</h3>
            <p>
              Manage roles in <Link to="/admin/users">Users</Link> or the workspace's own Team page.
            </p>
          </div>
          <table className="table">
            <tbody>
              {d.members.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.name ?? p.email}
                    <div className="xs muted">{p.email}</div>
                  </td>
                  <td>{roleLabel(p.role)}</td>
                  <td className="small muted">{p.lastLoginAt ? `Last sign-in ${fmtDate(p.lastLoginAt)}` : "Invited, not signed in yet"}</td>
                  <td>{p.disabled && <span className="pill red">Disabled</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}
